import { SHEET_DISPLAY_NAMES, type SheetType } from '../models/sheet-types';
import { resolveFieldLinkTarget } from './field-link-navigation.util';

import role8Mfrs from '../../assets/forms/role-210000-mfrs.form.json';
import role8Mpers from '../../assets/forms/role-210000-mpers.form.json';
import role12Mfrs from '../../assets/forms/role-310000-mfrs.form.json';
import role12Mpers from '../../assets/forms/role-310000-mpers.form.json';

/** One source row that declares a link INTO a given destination field. */
export interface InboundFieldLink {
  sourceSheet: SheetType;
  /** Display name, e.g. "Statement of Profit or Loss" — for the chooser dialog. */
  sourceSheetName: string;
  sourceQname: string;
  sourceLabel: string;
  taxonomy: 'mfrs' | 'mpers';
}

/**
 * The INVERSE of every `ui.linkTo` declaration in the shipped schemas.
 *
 * ## Why derive rather than declare
 *
 * A back-link is the mathematical inverse of a forward link. Declaring it a
 * second time on the destination control would create two sites that can
 * disagree — exactly the drift class the field-link consolidation removes — and
 * would owe a `docs/specifications/` record on two sheets instead of one.
 *
 * ## Why it is disabled
 *
 * Two-way links are a P.O. requirement for LATER, explicitly not now.
 * `inboundLinksFor` filters on `ui.linkTo.bidirectional === true`, which no
 * schema sets, so it returns `[]` everywhere. The capability ships unreachable by
 * construction rather than behind a switch somebody could flip early. Enabling it
 * is a schema edit with no code change — and that edit DOES owe a spec-sheet
 * record, on both the source and destination sheets.
 *
 * Only the two sheets that declare `ui.linkTo` today are imported. Both are
 * already in the bundle (their pure-validator rules import them), so this adds
 * no new bundle entry.
 */

/** Minimal structural view of a schema control. */
interface LinkableControl {
  qname?: unknown;
  label?: unknown;
  ui?: { linkTo?: { role?: string; qname?: string; bidirectional?: boolean } | null } | null;
}

/** Internal-only shape for an index entry — `bidirectional` never leaves this file. */
interface IndexedInboundFieldLink extends InboundFieldLink {
  bidirectional: boolean;
}

interface SchemaSource {
  json: unknown;
  sheet: SheetType;
  taxonomy: 'mfrs' | 'mpers';
}

const SOURCES: readonly SchemaSource[] = [
  { json: role8Mfrs, sheet: 'statement-of-financial-position', taxonomy: 'mfrs' },
  { json: role8Mpers, sheet: 'statement-of-financial-position', taxonomy: 'mpers' },
  { json: role12Mfrs, sheet: 'statement-of-profit-or-loss', taxonomy: 'mfrs' },
  { json: role12Mpers, sheet: 'statement-of-profit-or-loss', taxonomy: 'mpers' },
];

/** Stable key for a destination field. */
function edgeKey(sheet: SheetType, qname: string): string {
  return `${sheet}::${qname}`;
}

/** Every control anywhere in a schema tree (controls nest under sections on some sheets). */
function collectControls(json: unknown, into: LinkableControl[]): LinkableControl[] {
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node && typeof node === 'object') {
      const rec = node as LinkableControl & Record<string, unknown>;
      if (typeof rec.qname === 'string' && rec.ui) into.push(rec);
      Object.values(rec).forEach(walk);
    }
  };
  walk(json);
  return into;
}

function buildIndex(): Map<string, IndexedInboundFieldLink[]> {
  const index = new Map<string, IndexedInboundFieldLink[]>();

  for (const source of SOURCES) {
    for (const control of collectControls(source.json, [])) {
      const linkTo = control.ui?.linkTo;
      if (!linkTo?.role || !linkTo?.qname) continue;

      const target = resolveFieldLinkTarget(control.ui, String(control.qname));
      if (!target) continue;

      const key = edgeKey(target.sheet, target.scrollTo);
      const entry: IndexedInboundFieldLink = {
        sourceSheet: source.sheet,
        sourceSheetName: SHEET_DISPLAY_NAMES[source.sheet],
        sourceQname: String(control.qname),
        sourceLabel: typeof control.label === 'string' ? control.label : String(control.qname),
        taxonomy: source.taxonomy,
        bidirectional: linkTo.bidirectional === true,
      };

      const bucket = index.get(key);
      if (bucket) bucket.push(entry);
      else index.set(key, [entry]);
    }
  }

  return index;
}

const INDEX = buildIndex();

/**
 * EVERY inbound edge, ignoring the `bidirectional` opt-in. For the census spec
 * and the build guard only — never for rendering. Keyed `<sheet>::<qname>`.
 */
export function allInboundEdges(): ReadonlyMap<string, InboundFieldLink[]> {
  return INDEX;
}

/**
 * Inbound links a destination row should offer as BACK-links: only those whose
 * source opted in via `ui.linkTo.bidirectional`, optionally narrowed to the
 * active taxonomy so the benign MFRS/MPERS pair never looks like a collision.
 *
 * Returns `[]` for every input today, by design — see the file docblock.
 */
export function inboundLinksFor(
  destinationSheet: SheetType,
  destinationQname: string,
  taxonomy?: 'mfrs' | 'mpers'
): InboundFieldLink[] {
  const edges = INDEX.get(edgeKey(destinationSheet, destinationQname)) ?? [];
  return edges
    .filter((edge) => edge.bidirectional === true)
    .filter((edge) => !taxonomy || edge.taxonomy === taxonomy);
}
